import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { BudgetFilters, BudgetFormData } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Query keys
export const budgetKeys = {
  all: ["budgets"] as const,
  lists: () => [...budgetKeys.all, "list"] as const,
  list: (filters?: BudgetFilters) => [...budgetKeys.lists(), { filters }] as const,
  details: () => [...budgetKeys.all, "detail"] as const,
  detail: (id: number) => [...budgetKeys.details(), id] as const,
  months: () => [...budgetKeys.all, "month"] as const,
  month: (month: string) => [...budgetKeys.months(), month] as const,
  analyses: () => [...budgetKeys.all, "analysis"] as const,
  analysis: (month?: string, includeExcludedFromBudget?: boolean) =>
    [...budgetKeys.analyses(), { month, includeExcludedFromBudget }] as const,
  multiMonthAnalysis: (months: string[]) =>
    [...budgetKeys.analyses(), "multi", { months }] as const,
};

/**
 * Get budget vs actual analysis for a month (or current month if not specified)
 */
export function useBudgetAnalysis(month?: string, includeExcludedFromBudget?: boolean) {
  return useQuery({
    queryKey: budgetKeys.analysis(month, includeExcludedFromBudget),
    queryFn: () => apiClient.getBudgetAnalysis(month, includeExcludedFromBudget),
  });
}

/**
 * Upsert a budget (create or update if exists)
 */
export function useUpsertBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BudgetFormData) => apiClient.upsertBudget(data),
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: budgetKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: budgetKeys.month(variables.month),
      });
      queryClient.invalidateQueries({ queryKey: budgetKeys.analyses() });

      toast({
        title: "Success",
        description: "Budget saved successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save budget",
        variant: "destructive",
      });
    },
  });
}
